import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Form } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";

import { Button,  CircularProgress, Box } from "@mui/material";
import { getAllPartnerById, updatePartnerColorById } from "../../api/axios";
import styled from "styled-components";
import CmsCircularProgress from "../../Components/Common/CircularProgress";
import { showToast } from '../../Utills/toast';
import PhoneNumberInput from "../../Components/Common/PhoneInput/PhoneNumberInput";
import FormGroup from "../../Components/Common/FormGroup";

// Validation Schema
const validationSchema = Yup.object().shape({
  first_name: Yup.string()
    .trim()
    .required("First name is required."),
  last_name: Yup.string()
    .trim()
    .required("Last name is required."),
  email: Yup.string()
    .email("A valid email is required.")
    .required("Email is required."),
  phone_number: Yup.string()
    .required("Phone number is required.")
    .matches(/^\d{8,15}$/, "Please enter valid phone number."),
  sub_domain: Yup.string()
    .trim()
    .required("Subdomain is required."),
});

const GeneralSetting = () => {
  const [loading, setLoading] = useState(false);
  const { partnerId } = useParams();
  const [partnerData, setPartnerData] = useState(null);
  console.log("partnerData ==> ", partnerData);
  const [isLoading, setIsLoading] = useState(true);
  const [initialValues, setInitialValues] = useState({
    first_name: "",
    last_name: "",
    sub_domain: "",
    phone_number: "",
    email: "",
    logo: null,
  });

  useEffect(() => {
    setIsLoading(true);
    if (!Number(partnerId)) {
      setIsLoading(false);
      return;
    }
    getAllPartnerByIdData();
  }, [partnerId]);

  const getAllPartnerByIdData = async () => {
    try {
      const response = await getAllPartnerById(partnerId);
      if (response.data.data.length > 0) {
        const data = response.data.data[0];
        setPartnerData(data);
        setInitialValues({
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          sub_domain: data.sub_domain || "",
          phone_number: data.phone_number || "",
          email: data.email || "",
          logo: null,
        });
        console.log(data);
      } else {
        console.log(response.message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      setLoading(true);
      const formData = new FormData();
      const dataToSend = { ...values, id: partnerId };

      for (const [key, value] of Object.entries(dataToSend)) {
        if (value !== null) {
          formData.append(key, value);
        }
      }

      try {
        const response = await updatePartnerColorById(partnerId, formData);
        showToast({
          type: 'success',
          message: response.data.message || 'Your partner has been updated successfully!',
        });
        console.log(response.data);
      } catch (error) {
        const errorMessage = error?.response?.data?.message || 'Failed to update partner. Please try again.';
        showToast({
          type: 'error',
          message: errorMessage,
        });
        console.error(error);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to update partner. Please try again.';
      showToast({
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const ButtonGroup = styled.div`
    display: flex;
    justify-content: start;
    gap: 16px;
    
    @media (max-width: 576px) {
      flex-direction: column;
      gap: 12px;
    }
    
    button {
      width: auto;
      
      @media (max-width: 576px) {
        width: 100%;
      }
    }
  `;

  const ResponsiveThemeLogo = styled.div`
    border: 1px solid #d1d5db;
    width: 100%;
    padding: 10px;
    margin: 14px 0;
    border-radius: 10px;
    background-color: #f2f2f2;
    max-width: fit-content;
    
    @media (max-width: 576px) {
      margin: 10px 0;
      padding: 8px;
    }
    
    @media (max-width: 768px) {
      margin: 10px 6px;
    }
    
    img {
      margin-bottom: 23px;
      
      @media (max-width: 576px) {
        margin-bottom: 16px;
      }
    }
  `;

  const StyledForm = styled(Form)`
    @media (max-width: 576px) {
      padding: 0.5rem !important;
    }
    
    @media (max-width: 768px) {
      padding: 1rem !important;
    }
  `;

  const FormTitle = styled.h2`
    @media (max-width: 576px) {
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
    
    @media (max-width: 768px) {
      font-size: 1.75rem;
      margin-bottom: 1.5rem;
    }
  `;

  return (
    <div>
      {isLoading ? (
        <CmsCircularProgress />
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            getFieldProps,
          }) => (
            <StyledForm
              className="p-sm-4 p-2 shadow-md rounded-md"
              onSubmit={handleSubmit}
              noValidate
            >
              <FormTitle className="mb-3">General Settings</FormTitle>

              <FormGroup
                label="First Name"
                error={touched.first_name && errors.first_name}
                requiredField
              >
                <Form.Control
                  {...getFieldProps('first_name')}
                  type="text"
                  isInvalid={touched.first_name && !!errors.first_name}
                  style={{ 
                    height: "40px",
                  }}
                  placeholder="Enter First Name"
                />
              </FormGroup>

              <FormGroup
                label="Last Name"
                error={touched.last_name && errors.last_name}
                requiredField
              >
                <Form.Control
                  {...getFieldProps('last_name')}
                  type="text"
                  isInvalid={touched.last_name && !!errors.last_name}
                  style={{ 
                    height: "40px",
                  }}
                  placeholder="Enter Last Name"
                />
              </FormGroup>

              <FormGroup
                label="Email Address"
                error={touched.email && errors.email}
                requiredField
              >
                <Form.Control
                  {...getFieldProps('email')}
                  type="email"
                  isInvalid={touched.email && !!errors.email}
                />
              </FormGroup>

              <FormGroup
                label="Phone Number"
                error={touched.phone_number && errors.phone_number}
                requiredField
              >
                <PhoneNumberInput
                  label="Phone Number"
                  name="phone_number"
                  value={values.phone_number}
                  error={touched.phone_number && !!errors.phone_number}
                  helperText={touched.phone_number && errors.phone_number}
                  onChange={(value, country) => {
                    setFieldValue('phone_number', value);
                  }}
                />
              </FormGroup>

              <FormGroup
                label="Subdomain"
                error={touched.sub_domain && errors.sub_domain}
                requiredField
              >
                <Form.Control
                  {...getFieldProps('sub_domain')}
                  type="text"
                  isInvalid={touched.sub_domain && !!errors.sub_domain}
                />
              </FormGroup>

              <ResponsiveThemeLogo>
                {values?.logo && typeof values?.logo !== "string" ? (
                  <Box
                    component="div"
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <img
                      style={{
                        maxWidth: window.innerWidth < 576 ? "60vw" : window.innerWidth < 768 ? "40vw" : "13vw",
                        cursor: "pointer",
                        borderRadius: "8px",
                      }}
                      src={URL.createObjectURL(values?.logo)}
                      alt="image1"
                      onMouseEnter={(e) => {
                        e.target.style.opacity = 0.8;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = 1;
                      }}
                    />
                  </Box>
                ) : partnerData?.logo ? (
                  <Box
                    component="div"
                    sx={{ display: "flex", justifyContent: "center" }}
                  >
                    <img
                      style={{
                        maxWidth: window.innerWidth < 576 ? "60vw" : window.innerWidth < 768 ? "40vw" : "13vw",
                        cursor: "pointer",
                        borderRadius: "8px",
                      }}
                      src={partnerData?.logo}
                      alt="image1"
                      onMouseEnter={(e) => {
                        e.target.style.opacity = 0.8;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = 1;
                      }}
                    />
                  </Box>
                ) : (
                  ""
                )}
                <Box
                  component="div"
                  sx={{ display: "flex", justifyContent: "center" }}
                >
                  <Button
                    variant="contained"
                    component="label"
                    sx={{ 
                      width: "auto",
                    }}
                  >
                    Upload/Change Logo
                    <input
                      type="file"
                      name="logo"
                      onChange={(event) => {
                        setFieldValue("logo", event.currentTarget.files[0]);
                      }}
                      hidden
                    />
                  </Button>
                </Box>
              </ResponsiveThemeLogo>

              <ButtonGroup>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading || isSubmitting}
                >
                  {loading || isSubmitting ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </ButtonGroup>
            </StyledForm>
          )}
        </Formik>
      )}
    </div>
  );
};

export default GeneralSetting;
